import { VideoGame } from "@/interfaces/api";
import {
  ChallengeObj,
  ChallengeValidatorKey,
  ConstraintObj,
} from "@/interfaces/vidyaGrid";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const makeChallengeKey = (colC: ChallengeObj, rowC: ChallengeObj) =>
  `${colC.type}_${rowC.type}` as ChallengeValidatorKey;

export const pickConstraint = (constraints: ConstraintObj[]) => {
  const choosen = Math.floor(Math.random() * constraints.length);
  return constraints.slice(choosen, choosen + 1)[0];
};

export const compareReleaseYears = (
  firstReleaseDate: number,
  releaseYear: number
): boolean => {
  const releaseYearStart = Math.floor(
    new Date(`${releaseYear}.01.01`).getTime() / 1000
  );
  const releaseYearEnd = Math.floor(
    new Date(`${releaseYear}.12.31`).getTime() / 1000
  );
  return (
    firstReleaseDate >= releaseYearStart && firstReleaseDate <= releaseYearEnd
  );
};

export const validatorLiterals: Record<
  ChallengeValidatorKey,
  (game: VideoGame, colC: ChallengeObj, rowC: ChallengeObj) => boolean
> = {
  platform_platform: (game, colC, rowC) =>
    game.platforms.includes((colC.constraint as ConstraintObj).id) &&
    game.platforms.includes((rowC.constraint as ConstraintObj).id),
  platform_theme: (game, colC, rowC) =>
    game.platforms.includes((colC.constraint as ConstraintObj).id) &&
    game.themes.includes((rowC.constraint as ConstraintObj).id),
  theme_platform: (game, colC, rowC) =>
    game.themes.includes((colC.constraint as ConstraintObj).id) &&
    game.platforms.includes((rowC.constraint as ConstraintObj).id),
  theme_theme: (game, colC, rowC) =>
    game.themes.includes((colC.constraint as ConstraintObj).id) &&
    game.themes.includes((rowC.constraint as ConstraintObj).id),
  platform_genre: (game, colC, rowC) =>
    game.platforms.includes((colC.constraint as ConstraintObj).id) &&
    game.genres.includes((rowC.constraint as ConstraintObj).id),
  platform_year_of_release: (game, colC, rowC) =>
    game.platforms.includes((colC.constraint as ConstraintObj).id) &&
    compareReleaseYears(game.first_release_date, rowC.constraint as number),
  platform_rating: (game, colC, rowC) =>
    game.platforms.includes((colC.constraint as ConstraintObj).id) &&
    game.rating > (rowC.constraint as number),
  theme_genre: (game, colC, rowC) =>
    game.themes.includes((colC.constraint as ConstraintObj).id) &&
    game.genres.includes((rowC.constraint as ConstraintObj).id),
  theme_year_of_release: (game, colC, rowC) =>
    game.themes.includes((colC.constraint as ConstraintObj).id) &&
    compareReleaseYears(game.first_release_date, rowC.constraint as number),
  theme_rating: (game, colC, rowC) =>
    game.themes.includes((colC.constraint as ConstraintObj).id) &&
    game.rating > (rowC.constraint as number),
  genre_genre: (game, colC, rowC) =>
    game.genres.includes((colC.constraint as ConstraintObj).id) &&
    game.genres.includes((rowC.constraint as ConstraintObj).id),
  genre_theme: (game, colC, rowC) =>
    game.genres.includes((colC.constraint as ConstraintObj).id) &&
    game.themes.includes((rowC.constraint as ConstraintObj).id),
  genre_platform: (game, colC, rowC) =>
    game.genres.includes((colC.constraint as ConstraintObj).id) &&
    game.platforms.includes((rowC.constraint as ConstraintObj).id),
  genre_year_of_release: (game, colC, rowC) =>
    game.genres.includes((colC.constraint as ConstraintObj).id) &&
    compareReleaseYears(game.first_release_date, rowC.constraint as number),
  genre_rating: (game, colC, rowC) =>
    game.genres.includes((colC.constraint as ConstraintObj).id) &&
    game.rating > (rowC.constraint as number),
};

const loggify = (challenge: ChallengeObj) => {
  let normalizedConstraint: string;
  const { type, constraint } = challenge;

  if (constraint == undefined) normalizedConstraint = "none";
  else if (typeof constraint === "number")
    normalizedConstraint = constraint.toString();
  else normalizedConstraint = constraint.name;

  return {
    type,
    constraint: normalizedConstraint,
  };
};

export const loggifyChallenge = (challenge: ChallengeObj | ChallengeObj[]) => {
  if (Array.isArray(challenge)) return challenge.map((c) => loggify(c));

  return loggify(challenge);
};
